import React, { useEffect, useRef, useState } from 'react';
import { ASSAY_CAPTURES, ASSAY_STATES, type Assay, type AssayCapture, type AssayState } from '@mariana/labos-client';
import { limsClient, apiErrorMessage } from '../../api/limsClient';
import { metadataAttributes, metadataValuesOf, sameMetadata, type AssayMetadataValues } from '../../utils/assayMetadata';
import './AssayDefinition.css';

export const INSTRUMENT_NOTE = 'Instrument assays stay with engineering: a sync job writes their results and the seed owns their definition.';

interface FieldsProps {
  values: AssayMetadataValues;
  onChange: (values: AssayMetadataValues) => void;
  /** New assay page: the title is typed here and instrument capture is not offered. */
  creating?: boolean;
}

export const AssayMetadataFields: React.FC<FieldsProps> = ({ values, onChange, creating = false }) => {
  const set = (partial: Partial<AssayMetadataValues>) => onChange({ ...values, ...partial });
  const captures: AssayCapture[] = creating ? ['manual', 'upload'] : [...ASSAY_CAPTURES];
  return (
    <div className="assay-form">
      {creating && (
        <>
          <label htmlFor="assay-title">Title</label>
          <input id="assay-title" className="assay-input" value={values.title} onChange={e => set({ title: e.target.value })} placeholder="e.g. Chloride by titration" autoFocus />
        </>
      )}
      <label htmlFor="assay-sample-types">Sample types</label>
      <input id="assay-sample-types" className="assay-input" value={values.sampleTypes} onChange={e => set({ sampleTypes: e.target.value })} placeholder="comma-separated, e.g. pls, raffinate" />
      <label>Capture</label>
      <div>
        <div className="assay-radio-row">
          {captures.map(c => (
            <label key={c}>
              <input type="radio" name="assay-capture" value={c} checked={values.capture === c} onChange={() => set({ capture: c })} /> {c}
            </label>
          ))}
          {creating && (
            <label title={INSTRUMENT_NOTE} style={{ opacity: 0.6 }}>
              <input type="radio" name="assay-capture" value="instrument" disabled /> instrument
            </label>
          )}
        </div>
        {(creating || values.capture === 'instrument') && <div className="assay-note">{INSTRUMENT_NOTE}</div>}
      </div>
      <label htmlFor="assay-method">Method</label>
      <input id="assay-method" className="assay-input" value={values.method} onChange={e => set({ method: e.target.value })} placeholder='instrument or technique, e.g. "Titration"' />
      <label htmlFor="assay-description">Description</label>
      <textarea id="assay-description" className="assay-textarea" value={values.description} onChange={e => set({ description: e.target.value })} placeholder="what the assay measures and how results are derived" />
      <label htmlFor="assay-sop">SOP link</label>
      <input id="assay-sop" className="assay-input" type="url" value={values.sopUrl} onChange={e => set({ sopUrl: e.target.value })} placeholder="https://…" />
    </div>
  );
};

/** PATCH body for a state change: the legacy flags follow the state so Capital OS pickers stay in step. */
export const stateAttributes = (state: AssayState) => ({ state, active: state === 'active', visible: state === 'active' });

export const PROMOTION_NOTE = 'An admin promotes this to active once it is right.';

interface PanelProps {
  assay: Assay;
  isAdmin: boolean;
  onSaved: () => Promise<void> | void;
}

export const AssayMetadataPanel: React.FC<PanelProps> = ({ assay, isAdmin, onSaved }) => {
  const assayId = assay.id!;
  const [values, setValues] = useState<AssayMetadataValues>(() => metadataValuesOf(assay));
  const [saving, setSaving] = useState<null | 'fields' | AssayState>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // What the form last synced from the server. A refetch replaces the typed
  // text only when it means the same as what came back, so a just-saved form
  // picks up the server's formatting and unsaved edits survive.
  const saved = useRef<AssayMetadataValues>(metadataValuesOf(assay));
  const dirty = !sameMetadata(values, saved.current);
  const valuesRef = useRef(values);
  valuesRef.current = values;
  useEffect(() => {
    const next = metadataValuesOf(assay);
    saved.current = next;
    if (sameMetadata(valuesRef.current, next)) setValues(next);
  }, [assay]);

  const rawState = assay.attributes?.state;
  const state: AssayState | null = typeof rawState === 'string' && (ASSAY_STATES as readonly string[]).includes(rawState) ? (rawState as AssayState) : null;

  const patch = async (attrs: Record<string, unknown>, what: 'fields' | AssayState) => {
    setSaving(what);
    setError(null);
    try {
      await limsClient.updateEntityByType('assay', assayId, attrs);
      await onSaved();
      setSavedAt(Date.now());
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="assay-panel">
      <h3 className="assay-panel-heading">
        <span>Metadata &amp; lifecycle</span>
        <span className={`assay-state-badge ${state ?? ''}`}>{state ?? 'no state'}</span>
      </h3>
      {!isAdmin && <div className="assay-note" style={{ marginBottom: '0.5rem' }}>{PROMOTION_NOTE}</div>}
      {isAdmin && <div className="assay-actions" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
        {state !== 'active' && (
          <button type="button" className="assay-btn small success" disabled={saving !== null} onClick={() => patch(stateAttributes('active'), 'active')}
            title="Sets state to active and the legacy active/visible flags to true">
            {saving === 'active' ? 'Promoting…' : 'Promote to active'}
          </button>
        )}
        {state === 'active' && (
          <button type="button" className="assay-btn small danger" disabled={saving !== null} onClick={() => patch(stateAttributes('retired'), 'retired')}
            title="Sets state to retired and the legacy active/visible flags to false">
            {saving === 'retired' ? 'Retiring…' : 'Retire'}
          </button>
        )}
        {state !== 'draft' && state !== null && (
          <button type="button" className="assay-btn small" disabled={saving !== null} onClick={() => patch(stateAttributes('draft'), 'draft')}
            title="Back to draft: active/visible become false">
            {saving === 'draft' ? 'Saving…' : 'Back to draft'}
          </button>
        )}
      </div>}
      <AssayMetadataFields values={values} onChange={setValues} />
      <div className="assay-actions">
        <button type="button" className="assay-btn small primary" disabled={!dirty || saving !== null} onClick={() => patch(metadataAttributes(values), 'fields')}>
          {saving === 'fields' ? 'Saving…' : 'Save metadata'}
        </button>
        {dirty && <button type="button" className="assay-btn small" disabled={saving !== null} onClick={() => setValues(saved.current)}>Reset</button>}
        {!dirty && savedAt && !error && <span className="assay-success">Saved</span>}
      </div>
      {error && <div className="assay-error">{error}</div>}
    </div>
  );
};
